// src/components/admin/ClubSiteAdminSection.jsx
//
// Founder-only Club Website controls, embedded in /admin/clubs/:clubId.
// Sets the subdomain slug + enabled flag via the provisionClubSite callable
// and can force a site offline via forceUnpublishClubSite. Self-contained:
// loads the club's current siteSlug/siteEnabled itself so the host page
// only needs to pass clubId. See features/club-sites/ (D-9).

import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import toast from 'react-hot-toast';
import { db } from '@/firebase/firebase';
import { GlobeAltIcon, NoSymbolIcon } from '@heroicons/react/24/outline';

// Mirror the server reserved list + format (functions/modules/clubSites.js).
const RESERVED = new Set(['app', 'auth', 'staging', 'www', 'admin', 'api', 'mail', 'dev', 'support', 'help', 'status', 'cdn', 'assets', 'static']);
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;

export default function ClubSiteAdminSection({ clubId }) {
  const [slug, setSlug] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [current, setCurrent] = useState({ slug: null, enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'clubs', clubId));
        if (!alive) return;
        const d = snap.exists() ? snap.data() : {};
        setCurrent({ slug: d.siteSlug || null, enabled: d.siteEnabled === true });
        setSlug(d.siteSlug || '');
        setEnabled(d.siteEnabled !== false);
      } catch (e) {
        console.error('[ClubSiteAdminSection] load failed', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [clubId]);

  const slugError =
    !slug ? null
    : RESERVED.has(slug) ? 'Reserved word'
    : !SLUG_RE.test(slug) ? '3–40 chars, lowercase letters/numbers/hyphens, no leading/trailing hyphen'
    : null;

  const fns = getFunctions(undefined, 'us-central1');

  const save = async () => {
    if (slugError || !slug) { toast.error(slugError || 'Enter a slug'); return; }
    setSaving(true);
    try {
      await httpsCallable(fns, 'provisionClubSite')({ clubId, slug, enabled, reason: 'admin provision' });
      setCurrent({ slug, enabled });
      toast.success(`Site set: ${slug}.getscrimly.com`);
    } catch (e) {
      toast.error(e?.message || 'Could not save site settings');
    } finally {
      setSaving(false);
    }
  };

  const forceUnpublish = async () => {
    setSaving(true);
    try {
      await httpsCallable(fns, 'forceUnpublishClubSite')({ clubId, reason: 'admin force-unpublish' });
      setCurrent((c) => ({ ...c, enabled: false }));
      setEnabled(false);
      toast.success('Site taken offline');
    } catch (e) {
      toast.error(e?.message || 'Could not unpublish');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5 mb-4">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
        <GlobeAltIcon className="h-4 w-4" /> Club Website (founder only)
      </h2>

      {loading ? (
        <p className="text-sm text-zinc-400">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="field-slug" className="block text-sm font-medium text-zinc-700">Subdomain</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                id="field-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().trim())}
                placeholder="furyfc"
                className="block w-48 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
              <span className="text-sm text-zinc-500">.getscrimly.com</span>
            </div>
            {slugError && <p className="mt-1 text-xs text-rose-600">{slugError}</p>}
            {current.slug && (
              <p className="mt-1 text-xs text-zinc-500">
                Current: <span className="font-mono">{current.slug}.getscrimly.com</span> · {current.enabled ? 'enabled' : 'disabled'}
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Site enabled
          </label>

          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving || !!slugError || !slug}
              className="rounded-md bg-lime-400 px-4 py-2 text-sm font-semibold text-zinc-900 hover:brightness-95 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {current.slug && current.enabled && (
              <button
                onClick={forceUnpublish}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                <NoSymbolIcon className="h-4 w-4" /> Force unpublish
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
